import { useEffect, useRef, useState, useCallback } from "react";

const MEDALS = ["🥇", "🥈", "🥉", "4-е", "5-е", "6-е", "7-е", "8-е"];
const COMBO_COLORS = ["#ffffff", "#ffe600", "#ff8c00", "#ff3355"];
const COMBO_LABELS = ["", "🔥 СЕРИЯ ×1.5", "💥 ОГОНЬ ×2.0", "⚡ МАКСИМУМ ×3.0"];

// SVG Speedometer — pure declarative, no canvas issues
function Speedometer({ pct, trackColor, comboLevel, size = 200 }) {
  const cx = size / 2, cy = size / 2;
  const r = size * 0.40;
  const START_DEG = 135, END_DEG = 405; // 270° sweep
  const sweep = END_DEG - START_DEG;

  const comboColor = COMBO_COLORS[comboLevel] || trackColor;
  const needleDeg = START_DEG + sweep * Math.min(pct, 1);

  // Arc path helper
  function arc(cx, cy, r, startDeg, endDeg) {
    const s = (startDeg * Math.PI) / 180;
    const e = (endDeg * Math.PI) / 180;
    const x1 = cx + r * Math.cos(s), y1 = cy + r * Math.sin(s);
    const x2 = cx + r * Math.cos(e), y2 = cy + r * Math.sin(e);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  }

  // Tick marks
  const ticks = Array.from({ length: 11 }, (_, i) => {
    const deg = START_DEG + (sweep * i) / 10;
    const rad = (deg * Math.PI) / 180;
    const major = i % 2 === 0;
    const r1 = r * (major ? 0.78 : 0.83);
    const r2 = r * 0.90;
    return {
      x1: cx + r1 * Math.cos(rad), y1: cy + r1 * Math.sin(rad),
      x2: cx + r2 * Math.cos(rad), y2: cy + r2 * Math.sin(rad),
      major,
    };
  });

  // Needle
  const needleRad = (needleDeg * Math.PI) / 180;
  const nx = cx + r * 0.72 * Math.cos(needleRad);
  const ny = cy + r * 0.72 * Math.sin(needleRad);

  const speed = Math.round(pct * 300);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
      {/* Background circle */}
      <circle cx={cx} cy={cy} r={r * 1.08} fill="#0a0a0a" />
      <circle cx={cx} cy={cy} r={r * 1.08} fill="none" stroke="#1a1a1a" strokeWidth={2} />

      {/* Track arc (bg) */}
      <path d={arc(cx, cy, r * 0.78, START_DEG, END_DEG)}
        fill="none" stroke="#1e1e1e" strokeWidth={r * 0.14} strokeLinecap="round" />

      {/* Progress arc */}
      {pct > 0.005 && (
        <path d={arc(cx, cy, r * 0.78, START_DEG, START_DEG + sweep * Math.min(pct, 1))}
          fill="none" stroke={comboLevel > 0 ? comboColor : trackColor}
          strokeWidth={r * 0.13} strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 ${comboLevel > 0 ? 8 : 4}px ${comboLevel > 0 ? comboColor : trackColor})` }} />
      )}

      {/* Tick marks */}
      {ticks.map((t, i) => (
        <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={t.major ? "#444" : "#2a2a2a"} strokeWidth={t.major ? 2 : 1} />
      ))}

      {/* Needle */}
      <line x1={cx} y1={cy} x2={nx} y2={ny}
        stroke="#ff3355" strokeWidth={2.5} strokeLinecap="round"
        style={{ filter: "drop-shadow(0 0 4px #ff3355)" }} />

      {/* Center hub */}
      <circle cx={cx} cy={cy} r={r * 0.07} fill="#ff3355"
        style={{ filter: "drop-shadow(0 0 4px #ff3355)" }} />

      {/* Speed readout */}
      <text x={cx} y={cy + r * 0.32} textAnchor="middle" dominantBaseline="middle"
        fill="#fff" fontFamily="'Rubik Mono One', monospace" fontSize={r * 0.30}>
        {speed}
      </text>
      <text x={cx} y={cy + r * 0.52} textAnchor="middle" dominantBaseline="middle"
        fill="#444" fontFamily="'Nunito', sans-serif" fontSize={r * 0.14}>
        km/h
      </text>
    </svg>
  );
}

export default function TapracePlayer({
  track, pos, finishPos, comboLevel, multiplier, comboCount,
  finished, myPlace, roundResult, event, playerName, onTap,
}) {
  const [pressed, setPressed] = useState(false);
  const prevComboRef = useRef(comboLevel);
  const [comboFlash, setComboFlash] = useState(false);
  // Visual speed: spikes on tap, decays
  const speedRef = useRef(0);
  const targetRef = useRef(0);
  const [visualPct, setVisualPct] = useState(0);
  const rafRef = useRef(null);

  const FINISH = finishPos || 1000;
  const racePct = Math.min((pos || 0) / FINISH, 1);
  const trackColor = track?.color || "#ff8c00";
  const comboColor = COMBO_COLORS[comboLevel] || "#fff";

  // Animate speedometer needle
  useEffect(() => {
    function tick() {
      targetRef.current = Math.max(0, targetRef.current - 0.018);
      speedRef.current += (targetRef.current - speedRef.current) * 0.2;
      // Blend: 60% actual race position + 40% tap energy
      setVisualPct(racePct * 0.6 + speedRef.current * 0.4);
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [racePct]);

  // Combo flash + vibrate
  useEffect(() => {
    if (comboLevel > prevComboRef.current) {
      setComboFlash(true);
      setTimeout(() => setComboFlash(false), 350);
      if (navigator.vibrate) navigator.vibrate([25, 10, 25]);
    }
    prevComboRef.current = comboLevel;
  }, [comboLevel]);

  const handleTap = useCallback(() => {
    if (finished) return;
    onTap();
    targetRef.current = Math.min(1, targetRef.current + 0.20);
    if (navigator.vibrate) navigator.vibrate(12);
  }, [finished, onTap]);

  // ── Round result ────────────────────────────────────────────────────────────
  if (roundResult && !finished && pos < FINISH) {
    const myRank = roundResult.order?.indexOf(playerName) ?? -1;
    const myPts = roundResult.points_awarded?.[playerName] || 0;
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 animate-slide-up">
        <div style={{ fontSize: "3.5rem" }}>{MEDALS[myRank] || "🏁"}</div>
        <p className="font-display text-white" style={{ fontSize:"var(--fs-xl)" }}>
          {myRank === 0 ? "Ты первый! 🎉" : myRank === 1 ? "2-е место!" : myRank === 2 ? "3-е место!" : `${myRank + 1}-е место`}
        </p>
        <div style={{
          background: "#1a1030", border: "2.5px solid #ff8c00",
          borderRadius: "1rem", padding: "0.65rem 1.5rem", textAlign: "center",
        }}>
          <p className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-sm)" }}>Очки за раунд</p>
          <p className="font-display text-[#ff8c00]" style={{ fontSize:"var(--fs-2xl)" }}>+{myPts}</p>
        </div>
        <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-sm)" }}>Следующий раунд...</p>
      </div>
    );
  }

  // ── Finished ────────────────────────────────────────────────────────────────
  if (finished) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-slide-up">
        <div style={{ fontSize: "4rem" }}>{MEDALS[(myPlace || 1) - 1] || "🏁"}</div>
        <p className="font-display text-white" style={{ fontSize:"var(--fs-2xl)" }}>
          {myPlace === 1 ? "ПЕРВЫЙ! 🎉" : myPlace === 2 ? "2-е место!" : myPlace === 3 ? "3-е место!" : `${myPlace}-е место`}
        </p>
        <p className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-md)" }}>Ждём остальных...</p>
      </div>
    );
  }

  // ── Race ────────────────────────────────────────────────────────────────────
  const isNitro = event?.type === "nitro";
  const isOil = event?.type === "oil";

  return (
    <div className="flex-1 flex flex-col gap-2"
      style={{
        borderRadius: "1rem",
        padding: "0.2rem",
        background: comboFlash ? comboColor + "18" : "transparent",
        transition: "background 0.25s",
      }}
    >
      {/* Track info */}
      {track && (
        <div style={{
          background: track.bg || "#111",
          border: `1.5px solid ${trackColor}44`,
          borderRadius: "0.6rem",
          padding: "0.25rem 0.75rem",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span className="font-heading" style={{ color: trackColor, fontSize:"var(--fs-sm)" }}>{track.name}</span>
          <span className="font-heading text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>{track.desc}</span>
        </div>
      )}

      {/* Event notification */}
      {event && (
        <div style={{
          textAlign: "center",
          background: isNitro ? "#1a0a00" : "#001a1f",
          border: `2px solid ${isNitro ? "#ff8c00" : "#00f5ff"}`,
          borderRadius: "0.65rem", padding: "0.25rem",
        }}>
          <span className="font-display" style={{ color: isNitro ? "#ff8c00" : "#00f5ff", fontSize:"var(--fs-md)" }}>
            {isNitro ? "🔥 НИТРО! +200% на 3 сек" : "🛢️ МАСЛО! Замедление 3 сек"}
          </span>
        </div>
      )}

      {/* Speedometer + combo */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem" }}>
        <Speedometer pct={visualPct} trackColor={trackColor} comboLevel={comboLevel} size={180} />
        <div style={{ display: "flex", flexDirection: "column", gap:"var(--gap-sm)", minWidth: "5rem" }}>
          {/* Progress % */}
          <div style={{
            background: "#111", border: `1.5px solid ${trackColor}44`,
            borderRadius: "0.5rem", padding: "0.3rem 0.5rem", textAlign: "center",
          }}>
            <div className="font-heading text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>ПРОГРЕСС</div>
            <div className="font-display" style={{ color: trackColor, fontSize:"var(--fs-lg)" }}>
              {Math.round(racePct * 100)}%
            </div>
          </div>
          {/* Multiplier */}
          {multiplier > 1.01 && (
            <div style={{
              background: "#0a0a0a", border: `1.5px solid ${comboColor}`,
              borderRadius: "0.5rem", padding: "0.3rem 0.5rem", textAlign: "center",
              boxShadow: `0 0 8px ${comboColor}44`,
            }}>
              <div className="font-heading text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>БУСТ</div>
              <div className="font-display" style={{ color: comboColor, fontSize:"var(--fs-lg)" }}>
                x{typeof multiplier === "number" ? multiplier.toFixed(1) : multiplier}
              </div>
            </div>
          )}
          {/* Combo count */}
          {comboLevel > 0 && (
            <div style={{
              background: "#0a0a0a", border: `1.5px solid ${comboColor}44`,
              borderRadius: "0.5rem", padding: "0.3rem 0.5rem", textAlign: "center",
            }}>
              <div className="font-heading" style={{ color: comboColor, fontSize:"var(--fs-xs)" }}>СЕРИЯ</div>
              <div className="font-display" style={{ color: comboColor, fontSize:"var(--fs-lg)" }}>
                {comboCount}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Combo label */}
      {comboLevel > 0 && (
        <div style={{ textAlign: "center" }}>
          <span className="font-display" style={{
            color: comboColor, fontSize:"var(--fs-sm)",
            textShadow: `0 0 10px ${comboColor}`,
          }}>
            {COMBO_LABELS[comboLevel]}
          </span>
        </div>
      )}

      {/* GAS PEDAL */}
      <button
        onTouchStart={(e) => { e.preventDefault(); setPressed(true); handleTap(); }}
        onTouchEnd={() => setPressed(false)}
        onMouseDown={() => { setPressed(true); handleTap(); }}
        onMouseUp={() => setPressed(false)}
        onMouseLeave={() => setPressed(false)}
        style={{
          flex: 1,
          minHeight: "6rem",
          position: "relative",
          overflow: "hidden",
          background: isNitro
            ? `radial-gradient(circle at 50% 65%, #ff8c0055, ${track?.bg || "#1a0a00"})`
            : `radial-gradient(circle at 50% 65%, ${trackColor}33, ${track?.bg || "#111"})`,
          border: `4px solid ${comboColor}`,
          borderRadius: "1.5rem",
          boxShadow: [
            `0 0 ${comboLevel * 14 + 6}px ${comboColor}55`,
            pressed ? "inset 0 6px 14px rgba(0,0,0,0.55)" : "inset 0 -6px 0 rgba(0,0,0,0.35)",
            "4px 4px 0 #000",
          ].join(", "),
          cursor: "pointer",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: "0.25rem",
          WebkitUserSelect: "none", userSelect: "none",
          WebkitTapHighlightColor: "transparent",
          touchAction: "manipulation",
          transform: pressed ? "scale(0.96) translateY(4px)" : "scale(1) translateY(0)",
          transition: "transform 0.07s ease, box-shadow 0.07s ease",
        }}
      >
        {isNitro && (
          <div style={{
            position: "absolute", inset: 3, borderRadius: "1.1rem",
            border: "2.5px solid #ff8c00", opacity: 0.6,
            animation: "pulse 0.45s ease-in-out infinite",
            pointerEvents: "none",
          }} />
        )}

        <span style={{
          fontSize: "3rem", lineHeight: 1,
          filter: comboLevel > 0 ? `drop-shadow(0 0 10px ${comboColor})` : "none",
          transform: pressed ? "scale(0.9)" : "scale(1)",
          transition: "transform 0.07s",
        }}>🏎️</span>

        <span className="font-display" style={{
          color: isNitro ? "#ff8c00" : isOil ? "#00f5ff" : trackColor,
          fontSize:"var(--fs-2xl)",
          textShadow: `0 0 16px ${trackColor}`,
        }}>
          {isNitro ? "НИТРО!" : isOil ? "СКОЛЬЗКО!" : "ЖМИ!"}
        </span>

        <span className="font-heading text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>
          {comboLevel === 0 ? "Тапай быстро для комбо!" : "Не останавливайся!"}
        </span>
      </button>
    </div>
  );
}